/**
 * [PROD-86] Completion confirmation for tool-using turns. When the agent
 * finishes a turn that performed actions, the chat renders a compact
 * success/stopped chip under the reply so the user KNOWS the work landed
 * (no more "did you do it?"). Errors are excluded here — the message-level
 * ErrorDisplay already owns that state. Pure so it unit-tests cheaply.
 */

export interface CompletionSummaryMessage {
  role: string
  status?: string
  toolCalls?: Array<unknown> | undefined
  errorMessage?: string
  /** Set by the engines' onCancelled — a user-interrupted turn. */
  cancelled?: boolean
  tokensOut?: number
  costUsd?: number
}

export interface CompletionSummary {
  kind: 'success' | 'stopped'
  label: string
  /** Secondary text (tokens/cost) when usage landed with the completion. */
  detail?: string
}

export function completionSummaryFor(m: CompletionSummaryMessage): CompletionSummary | null {
  if (m.role !== 'assistant') {
    return null
  }
  const actionCount = m.toolCalls?.length ?? 0
  if (actionCount === 0) {
    // Plain Q&A turn — a chip would be noise.
    return null
  }
  if (m.errorMessage) {
    // ErrorDisplay owns the failure surface; don't double-report.
    return null
  }
  if (m.status === 'streaming') {
    return null
  }
  const actions = `${actionCount} action${actionCount === 1 ? '' : 's'}`
  if (m.cancelled) {
    return { kind: 'stopped', label: `Stopped — ${actions} ran before interrupt` }
  }
  const detail =
    typeof m.costUsd === 'number' && m.costUsd > 0
      ? `$${m.costUsd.toFixed(m.costUsd < 0.1 ? 3 : 2)}`
      : typeof m.tokensOut === 'number' && m.tokensOut > 0
        ? `${m.tokensOut.toLocaleString()} tokens`
        : undefined
  return { kind: 'success', label: `Done — ${actions} completed`, ...(detail ? { detail } : {}) }
}
